import { useEffect, useState } from "react";
import { Button, Dialog, Input, Select } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

interface State {
  open: boolean;
  edit?: {
    id: string;
    kind: string;
    label: string | null;
    addition?: string | null;
    street: string | null;
    zip: string | null;
    city: string | null;
    countryCode: string;
    isPrimary: boolean;
  };
}

const EMPTY = {
  kind: "billing",
  label: "",
  addition: "",
  street: "",
  zip: "",
  city: "",
  countryCode: "AT",
  isPrimary: false,
};

export function AddressDialog({
  customerId,
  state,
  onClose,
}: {
  customerId: string;
  state: State;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const [f, setF] = useState<Record<string, any>>(EMPTY);

  useEffect(() => {
    if (state.open) {
      setF(
        state.edit
          ? {
              kind: state.edit.kind,
              label: state.edit.label ?? "",
              addition: state.edit.addition ?? "",
              street: state.edit.street ?? "",
              zip: state.edit.zip ?? "",
              city: state.edit.city ?? "",
              countryCode: state.edit.countryCode,
              isPrimary: state.edit.isPrimary,
            }
          : EMPTY,
      );
    }
  }, [state.open, state.edit]);

  const done = () => {
    utils.customer.get.invalidate({ id: customerId });
    utils.customer.list.invalidate();
    onClose();
  };
  const add = trpc.customer.addAddress.useMutation({ onSuccess: done });
  const upd = trpc.customer.updateAddress.useMutation({ onSuccess: done });
  const busy = add.isPending || upd.isPending;

  const submit = () => {
    // Am Kunden werden nur noch Rechnungsadressen angelegt (Standorte/Durchführungsorte sind Objekte);
    // bereits vorhandene andere Adressarten behalten beim Bearbeiten ihre Art.
    const payload = {
      kind: (state.edit ? f.kind : "billing") as "billing" | "site" | "postal",
      label: f.label || null,
      addition: f.addition || null,
      street: f.street || null,
      zip: f.zip || null,
      city: f.city || null,
      countryCode: (f.countryCode || "AT").toUpperCase().slice(0, 2),
      isPrimary: !!f.isPrimary,
    };
    if (state.edit) upd.mutate({ id: state.edit.id, customerId, ...payload });
    else add.mutate({ customerId, ...payload });
  };

  return (
    <Dialog
      open={state.open}
      onOpenChange={(v) => !v && onClose()}
      title={
        state.edit
          ? "Rechnungsadresse bearbeiten"
          : "Rechnungsadresse hinzufügen"
      }
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={submit} loading={busy}>
            {state.edit ? "Speichern" : "Hinzufügen"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Bezeichnung (optional)"
          value={f.label}
          onChange={(e) => setF((s) => ({ ...s, label: e.target.value }))}
        />
        <div className="col-span-2">
          <Input
            label="Adresszusatz (optional, steht im PDF unter dem Namen)"
            placeholder="z. B. z. H. Frau Högler"
            value={f.addition}
            onChange={(e) => setF((s) => ({ ...s, addition: e.target.value }))}
          />
        </div>
        <div className="col-span-2">
          <Input
            label="Straße"
            value={f.street}
            onChange={(e) => setF((s) => ({ ...s, street: e.target.value }))}
          />
        </div>
        <div className="grid grid-cols-[100px_1fr] gap-2">
          <Input
            label="PLZ"
            value={f.zip}
            onChange={(e) => setF((s) => ({ ...s, zip: e.target.value }))}
          />
          <Input
            label="Ort"
            value={f.city}
            onChange={(e) => setF((s) => ({ ...s, city: e.target.value }))}
          />
        </div>
        <Input
          label="Land (ISO-2)"
          value={f.countryCode}
          onChange={(e) => setF((s) => ({ ...s, countryCode: e.target.value }))}
        />
        <label className="col-span-2 flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={!!f.isPrimary}
            onChange={(e) =>
              setF((s) => ({ ...s, isPrimary: e.target.checked }))
            }
          />
          primäre Rechnungsadresse
        </label>
      </div>
    </Dialog>
  );
}
